import { BookMarked, ChevronUp, Megaphone, Plus } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useAddToBoard } from "./use-add-to-board";

interface AddToBoardProps {
  /** The deals being filed — applicant company ids, i.e. DealTransformed.id. */
  companyIds: string[];
  /** Called after a successful change, e.g. to clear a bulk selection. */
  onDone?: () => void;
}

/**
 * The body of the "Add to board" menu: the Raiseboard, then every lookbook,
 * then a shortcut that creates one holding the current selection.
 *
 * Rendered as checkbox items rather than plain actions so the same menu that
 * files a deal can take it back off — the Raiseboard page has no add-to-
 * lookbook control any more, so this is the only place membership is edited.
 */
function AddToBoardItems({ companyIds, onDone }: AddToBoardProps) {
  const {
    lookbooksEnabled,
    lookbooks,
    isPending,
    allOnRaiseBoard,
    allInLookbook,
    setRaiseBoard,
    setLookbook,
    createLookbookWith,
  } = useAddToBoard(onDone);

  return (
    <>
      <DropdownMenuCheckboxItem
        checked={allOnRaiseBoard(companyIds)}
        disabled={isPending || companyIds.length === 0}
        onSelect={(e) => e.preventDefault()}
        onCheckedChange={(checked) =>
          setRaiseBoard(companyIds, checked === true)
        }
      >
        <Megaphone className="mr-2 h-4 w-4 shrink-0" />
        <span className="truncate">Raiseboard</span>
      </DropdownMenuCheckboxItem>

      {lookbooksEnabled && (
        <>
          {lookbooks.length > 0 && <DropdownMenuSeparator />}
          {lookbooks.map((lookbook) => (
            <DropdownMenuCheckboxItem
              key={lookbook._id}
              checked={allInLookbook(lookbook, companyIds)}
              disabled={isPending || companyIds.length === 0}
              // Removing keeps the menu open, the way every other row here
              // does. Adding lets it close, because it opens the "what should
              // this show?" dialog and the menu would sit on top of it.
              onSelect={(e) => {
                if (allInLookbook(lookbook, companyIds)) e.preventDefault();
              }}
              onCheckedChange={(checked) =>
                setLookbook(lookbook, companyIds, checked === true)
              }
            >
              <BookMarked className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">{lookbook.name}</span>
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          {/* inset lines the icon up with the checkbox rows above, whose
              tick indicator already occupies the first 8px. */}
          <DropdownMenuItem
            inset
            disabled={isPending || companyIds.length === 0}
            onSelect={() => createLookbookWith(companyIds)}
          >
            <Plus className="h-4 w-4 shrink-0" />
            Add lookbook
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}

/**
 * The bulk-selection version: a toolbar button that opens upward, since the
 * selection toolbar itself is pinned to the bottom of the viewport.
 */
export function AddToBoardButton({ companyIds, onDone }: AddToBoardProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
        >
          <Megaphone className="h-4 w-4" />
          Add to board
          <ChevronUp className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="center" className="w-56">
        <AddToBoardItems companyIds={companyIds} onDone={onDone} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The single-deal version, for the row/card overflow menu. Must be rendered
 * inside a DropdownMenuContent.
 */
export function AddToBoardSubMenu({ companyIds, onDone }: AddToBoardProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>Add to board</DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-56">
        <AddToBoardItems companyIds={companyIds} onDone={onDone} />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
